/**
 * Button.
 *
 * Renders as an <a> when given `to` or `href`, so navigation stays keyboard-
 * and screen-reader-correct rather than a div with an onClick.
 */

import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './Button.module.css';

export const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    to,
    href,
    type = 'button',
    loading = false,
    disabled = false,
    fullWidth = false,
    iconLeft = null,
    iconRight = null,
    className = '',
    ...rest
  },
  ref,
) {
  const classes = [
    styles.button,
    styles[variant],
    styles[size],
    fullWidth ? styles.fullWidth : '',
    loading ? styles.loading : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {iconLeft && !loading && <span className={styles.icon}>{iconLeft}</span>}
      <span className={styles.label}>{children}</span>
      {iconRight && <span className={styles.icon}>{iconRight}</span>}
    </>
  );

  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        ref={ref}
        href={href}
        className={classes}
        target={rest.target ?? '_blank'}
        rel={rest.target === '_self' ? undefined : 'noreferrer noopener'}
        {...rest}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
});
